// eba.js is the anonymous device-key identity: a self-signed ES256 JWS carrying its own P-256 key,
// with iss == sub == a GUID derived from that key. Losing the key orphans the account.
// It must match the server's verifyAnonToken / guidOfCoords:
//   GUID    = uuid_8_4_4_4_12( hex( SHA-256(xBytes ++ yBytes) ).slice(0,32) )

const subtle = globalThis.crypto.subtle;

export function b64url(bytes) {
  let s = "";
  for (const b of bytes) s += String.fromCharCode(b);
  return btoa(s).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

export function b64urlDecode(str) {
  const b64 = str.replace(/-/g, "+").replace(/_/g, "/");
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

function hex(bytes) {
  let s = "";
  for (const b of bytes) s += b.toString(16).padStart(2, "0");
  return s;
}

// Derive the anon GUID; the server recomputes it, so EBA needs no server state.
export async function deriveGuid(xBytes, yBytes) {
  const cat = new Uint8Array(xBytes.length + yBytes.length);
  cat.set(xBytes, 0);
  cat.set(yBytes, xBytes.length);
  const dig = new Uint8Array(await subtle.digest("SHA-256", cat));
  const h = hex(dig).slice(0, 32);
  return (
    h.slice(0, 8) + "-" + h.slice(8, 12) + "-" + h.slice(12, 16) + "-" +
    h.slice(16, 20) + "-" + h.slice(20, 32)
  );
}

export async function deviceIdentity(keyPair) {
  const jwk = await subtle.exportKey("jwk", keyPair.publicKey);
  const xBytes = b64urlDecode(jwk.x);
  const yBytes = b64urlDecode(jwk.y);
  const guid = await deriveGuid(xBytes, yBytes);
  return { keyPair, x: jwk.x, y: jwk.y, guid };
}

// The private key is non-extractable; IndexedDB still persists the CryptoKey handle by structured clone.
export async function generateDeviceKey() {
  const keyPair = await subtle.generateKey(
    { name: "ECDSA", namedCurve: "P-256" },
    false,
    ["sign", "verify"]
  );
  return await deviceIdentity(keyPair);
}

// Self-sign an anon token valid [now, now+ttl) and return the compact JWS.
export async function signAnonToken(identity, nowSec, ttlSec) {
  const header = {
    alg: "ES256",
    typ: "JWT",
    kind: "ANON",
    jwk: { kty: "EC", crv: "P-256", x: identity.x, y: identity.y },
  };
  const claims = {
    iss: identity.guid,
    sub: identity.guid,
    iat: nowSec,
    exp: nowSec + ttlSec,
  };
  const enc = new TextEncoder();
  const seg = (obj) => b64url(enc.encode(JSON.stringify(obj)));
  const signingInput = seg(header) + "." + seg(claims);
  const sig = new Uint8Array(
    await subtle.sign(
      { name: "ECDSA", hash: "SHA-256" },
      identity.keyPair.privateKey,
      enc.encode(signingInput)
    )
  );
  return signingInput + "." + b64url(sig);
}
